// Tour System - Complete Index
// Import and export all tour definitions

import { ftuxTour } from './ftux-tour.js';
import { contactsTour } from './contacts-tour.js';
import { conversationsTour } from './conversations-tour.js';
import { calendarTour } from './calendar-tour.js';
import { opportunitiesTour } from './opportunities-tour.js';
import { marketingTour } from './marketing-tour.js';
import { sitesTour } from './sites-tour.js';
import { automationTour } from './automation-tour.js';
import { settingsTour } from './settings-tour.js';
import { reportingTour } from './reporting-tour.js';
import { paymentsTour } from './payments-tour.js';
import { reputationTour } from './reputation-tour.js';
import { aiEmployeeTour } from './ai-employee-tour.js';
import { membershipsTour } from './memberships-tour.js';
import { integrationsTour } from './integrations-tour.js';
import { dashboardsTour } from './dashboards-tour.js';
import { mediaStorageTour } from './media-storage-tour.js';
import { mobileAppTour } from './mobile-app-tour.js';
import { phoneTour } from './phone-tour.js';
import { emailSetupTour } from './email-setup-tour.js';
import { tasksTour } from './tasks-tour.js';
import { documentsTour } from './documents-tour.js';
import { ecommerceTour } from './ecommerce-tour.js';
import { clientPortalTour } from './client-portal-tour.js';
import { certificatesTour } from './certificates-tour.js';
import { crmRecordsTour } from './crm-records-tour.js';
import { companiesTour } from './companies-tour.js';
import { customObjectsTour } from './custom-objects-tour.js';
import { mergeFieldsTour } from './merge-fields-tour.js';
import { notificationsTour } from './notifications-tour.js';
import { logicFulfillmentTour } from './logic-fulfillment-tour.js';

// Export all tours as a single object
export const allTours = {
  ftux: ftuxTour,
  contacts: contactsTour,
  conversations: conversationsTour,
  calendar: calendarTour,
  opportunities: opportunitiesTour,
  marketing: marketingTour,
  sites: sitesTour,
  automation: automationTour,
  settings: settingsTour,
  reporting: reportingTour,
  'payments': paymentsTour,
  'reputation': reputationTour,
  'ai-employee': aiEmployeeTour,
  'memberships': membershipsTour,
  'integrations': integrationsTour,
  'dashboards': dashboardsTour,
  'media-storage': mediaStorageTour,
  'mobile-app': mobileAppTour,
  'phone': phoneTour,
  'email-setup': emailSetupTour,
  'tasks': tasksTour,
  'documents': documentsTour,
  'ecommerce': ecommerceTour,
  'client-portal': clientPortalTour,
  'certificates': certificatesTour,
  'crm-records': crmRecordsTour,
  'companies': companiesTour,
  'custom-objects': customObjectsTour,
  'merge-fields': mergeFieldsTour,
  'notifications': notificationsTour,
  'logic-fulfillment': logicFulfillmentTour
};

// Tour metadata for reference
export const tourMetadata = {
  total: 31,
  totalSteps: 131, // Approximate total across all tours
  estimatedTotalDuration: '50 minutes',
  
  tours: [
    {
      id: 'ftux',
      name: 'Platform Overview',
      category: 'onboarding',
      priority: 1,
      steps: 12,
      duration: '4 minutes',
      autoStart: true,
      prerequisites: []
    },
    {
      id: 'contacts',
      name: 'Contacts Module',
      category: 'feature',
      priority: 2,
      steps: 14,
      duration: '5 minutes',
      autoStart: false,
      prerequisites: ['ftux']
    },
    {
      id: 'conversations',
      name: 'Conversations Module',
      category: 'feature',
      priority: 3,
      steps: 13,
      duration: '4 minutes',
      autoStart: false,
      prerequisites: ['ftux']
    },
    {
      id: 'calendar',
      name: 'Calendar Module',
      category: 'feature',
      priority: 4,
      steps: 13,
      duration: '4 minutes',
      autoStart: false,
      prerequisites: ['ftux']
    },
    {
      id: 'opportunities',
      name: 'Opportunities (Pipeline)',
      category: 'feature',
      priority: 5,
      steps: 14,
      duration: '5 minutes',
      autoStart: false,
      prerequisites: ['ftux']
    },
    {
      id: 'marketing',
      name: 'Marketing Module',
      category: 'feature',
      priority: 6,
      steps: 14,
      duration: '6 minutes',
      autoStart: false,
      prerequisites: ['ftux', 'contacts']
    },
    {
      id: 'sites',
      name: 'Sites & Funnels',
      category: 'feature',
      priority: 7,
      steps: 14,
      duration: '6 minutes',
      autoStart: false,
      prerequisites: ['ftux']
    },
    {
      id: 'automation',
      name: 'Automation & Workflows',
      category: 'feature',
      priority: 8,
      steps: 17,
      duration: '8 minutes',
      autoStart: false,
      prerequisites: ['ftux', 'contacts']
    },
    {
      id: 'settings',
      name: 'Settings & Configuration',
      category: 'feature',
      priority: 9,
      steps: 13,
      duration: '5 minutes',
      autoStart: false,
      prerequisites: ['ftux']
    },
    {
      id: 'reporting',
      name: 'Reporting & Analytics',
      category: 'feature',
      priority: 10,
      steps: 13,
      duration: '4 minutes',
      autoStart: false,
      prerequisites: ['ftux']
    }
  ]
};

// Suggested tour sequences
export const tourSequences = {
  // For complete beginners
  beginner: [
    'ftux',
    'contacts',
    'conversations',
    'calendar'
  ],
  
  // For sales-focused users
  sales: [
    'ftux',
    'contacts',
    'opportunities',
    'calendar',
    'conversations'
  ],
  
  // For marketing-focused users
  marketing: [
    'ftux',
    'contacts',
    'marketing',
    'sites',
    'automation'
  ],
  
  // Complete platform mastery
  complete: [
    'ftux',
    'contacts',
    'conversations',
    'calendar',
    'opportunities',
    'marketing',
    'sites',
    'automation',
    'settings',
    'reporting'
  ]
};

// Helper function to get tour by ID
export function getTour(tourId) {
  return allTours[tourId] || null;
}

// Helper function to get next suggested tour
export function getNextTour(completedTours = [], sequence = 'complete') {
  const suggestedSequence = tourSequences[sequence] || tourSequences.complete;
  
  for (const tourId of suggestedSequence) {
    if (!completedTours.includes(tourId)) {
      return getTour(tourId);
    }
  }
  
  return null; // All tours completed!
}

// Helper function to check prerequisites
export function canStartTour(tourId, completedTours = []) {
  const tour = getTour(tourId);
  if (!tour) return false;
  
  const metadata = tourMetadata.tours.find(t => t.id === tourId);
  if (!metadata) return true; // No metadata = no prerequisites
  
  // Check if all prerequisites are completed
  return metadata.prerequisites.every(prereq => completedTours.includes(prereq));
}

export default allTours;
